import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

import { classNames } from "@helpers/css";

// A subtle inline pill that shows the picked skill as `/slug` plus a small
// scope hint. Personal skills get no tag (they're the default scope);
// everything else (workspace + built-in platform defaults) lives in the org,
// so it just reads "org".
const SkillMentionChip = ({
  node,
}: {
  node: { attrs: Record<string, any> };
}) => {
  const slug: string = node.attrs.slug ?? "";
  const scope: string = node.attrs.scope ?? "PERSONAL";
  const scopeLabel = scope === "PERSONAL" ? null : "org";
  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      className={classNames(
        "inline-flex items-center gap-1 rounded bg-blue-50 px-1 align-baseline",
      )}
    >
      <span className="font-mono text-blue-link">/{slug}</span>
      {scopeLabel && (
        <span className="rounded bg-blue-100 px-1 text-[10px] font-medium leading-none text-blue-600">
          {scopeLabel}
        </span>
      )}
    </NodeViewWrapper>
  );
};

const SkillMention = Node.create({
  name: "skillMention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      slug: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-skill-slug") ?? "",
        renderHTML: (attributes) => ({
          "data-skill-slug": attributes.slug,
        }),
      },
      skillId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-skill-id"),
        renderHTML: (attributes) =>
          attributes.skillId ? { "data-skill-id": attributes.skillId } : {},
      },
      scope: {
        default: "PERSONAL",
        parseHTML: (element) =>
          element.getAttribute("data-skill-scope") ?? "PERSONAL",
        renderHTML: (attributes) => ({
          "data-skill-scope": attributes.scope,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-skill-mention]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes({ "data-skill-mention": "" }, HTMLAttributes),
      `/${HTMLAttributes["data-skill-slug"] ?? ""}`,
    ];
  },

  // CRITICAL: editor.getText() is the message text the input sends. Emitting
  // `/slug` here keeps the leading command in the plain text so the backend
  // still parses the typed-command precedence even when a chip is used.
  renderText({ node }) {
    return `/${node.attrs.slug}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(SkillMentionChip);
  },
});

export default SkillMention;
